import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { resolveLogoSrc } from "../utils/resolveLogoSrc";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import iziToast from "izitoast";
import "izitoast/dist/css/iziToast.min.css";
import Select from "react-select";
import "./CustomerReceipts.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import VisibilityIcon from "@mui/icons-material/Visibility";

function CustomerReceipts() {
  let paymentTypeOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Card", value: "Card" },
    { label: "Check", value: "Check" },
    { label: "Online", value: "Online" },
  ];
  const getLocalDateInputValue = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeTab, setActiveTab] = useState("receipts");
  const [bulkPaymentSearch, setBulkPaymentSearch] = useState("");
  const [bulkPaymentFromDate, setBulkPaymentFromDate] = useState("");
  const [bulkPaymentToDate, setBulkPaymentToDate] = useState("");
  const [bulkPayments, setBulkPayments] = useState([]);
  const [bulkPaymentsLoading, setBulkPaymentsLoading] = useState(true);
  const [perms, setPerms] = useState({});
  const [receipts, setReceipts] = useState([]);
  const { customer_id, customer_name } = useParams();
  const [isPaymentModelVisible, setIsPaymentModelVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [paymentType, setPaymentType] = useState();
  const [paymentDate, setPaymentDate] = useState(getLocalDateInputValue());
  const [paymentNote, setPaymentNote] = useState("");
  const [bulkPaymentSaving, setBulkPaymentSaving] = useState(false);
  const [printSummaryMode, setPrintSummaryMode] = useState("unpaid");
  const [paidSummaryReceipts, setPaidSummaryReceipts] = useState([]);
  // let unpaidReceipts = [];
  let [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [selectedReceiptIds, setSelectedReceiptIds] = useState(new Set());
  

  let navigate = useNavigate();
  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../images/logo-light.png");

  const getReceipts = useCallback(() => {
    axios
      .get(`${Helpers.baseUrl}/receipts/customer/${customer_id}`, Helpers.headers)
      .then((response) => {
        setReceipts(response.data.reverse());
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      });
  }, [customer_id, navigate]);

  const getBulkPayments = useCallback(() => {
    setBulkPaymentsLoading(true);
    axios
      .get(
        `${Helpers.baseUrl}/receipts/bulk-payments?customer_id=${encodeURIComponent(customer_id)}`,
        Helpers.headers,
      )
      .then((response) => {
        setBulkPayments(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setBulkPaymentsLoading(false));
  }, [customer_id, navigate]);



  const handleDelete = (receiptId) => {
    iziToast.warning({
      timeout: 20000,
      close: false,
      overlay: true,
      displayMode: "once",
      zindex: 9999,
      title: "",
      message: "Are you sure to delete this receipt",
      position: "center",
      buttons: [
        [
          "<button><b>YES</b></button>",
          function (instance, toast) {
            axios
              .get(
                `${Helpers.baseUrl}/receipts/delete/${receiptId}`,
                Helpers.headers
              )
              .then((response) => {
                getReceipts();
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
                Helpers.toast("success", "Receipt deleted successfully");
              })
              .catch((error) => {
                Helpers.unauthenticated(error, navigate);
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
              });
          },
          true,
        ],
        [
          "<button>NO</button>",
          function (instance, toast) {
            instance.hide({ transitionOut: "fadeOut" }, toast, "button");
          },
        ],
      ],
    });
  };

  const getPerms = () => {
    try {
      const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
      const perm = Array.isArray(permissions)
        ? permissions.find((permission) => permission.tab_link === "/receipts")
        : null;
      setPerms(perm || {});
    } catch (error) {
      setPerms({});
    }
  }

  const handleCheckBox = (id) => {
    setSelectedReceiptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleAll = (checked) => {
    setSelectedReceiptIds((prev) => {
      const next = new Set(prev);
      filteredUnpaidReceiptIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const handleBulkUpdate = () => {
    if (selectedUnpaidReceipts.length === 0) return;
    setErrors({});
    setPaymentDate((prev) => prev || getLocalDateInputValue());
    setIsPaymentModelVisible(true);
  }

  const handlePrintSelected = () => {
    if (selectedUnpaidReceipts.length === 0) return;
    setPrintSummaryMode("unpaid");
    setPaidSummaryReceipts([]);
    setTimeout(() => window.print(), 0);
  };

  const resetBulkPaymentForm = () => {
    setSelectedPaymentType(null);
    setPaymentType(undefined);
    setPaymentDate(getLocalDateInputValue());
    setPaymentNote("");
    setErrors({});
  };

  const closeBulkPaymentModal = () => {
    if (bulkPaymentSaving) return;
    setIsPaymentModelVisible(false);
    resetBulkPaymentForm();
  };
  
  const makeBulkPayment = () => {
    if (bulkPaymentSaving) return;

    const nextErrors = {};
    if (selectedUnpaidReceipts.length === 0) {
      nextErrors.receipts = "Please select at least one unpaid receipt";
    }
    if (!paymentType) {
      nextErrors.payment_type = "Please Select a Payment Type";
    }
    if (!paymentDate) {
      nextErrors.payment_date = "Please Enter Payment Date";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      Helpers.toast("error", "Fix the errors to continue");
      return;
    }

    setBulkPaymentSaving(true);
    setErrors({});
    const receiptsBeingPaid = selectedUnpaidReceipts;
    const submittedPaymentType = paymentType;
    const submittedPaymentDate = paymentDate;

    const payments = {
      receipts: receiptsBeingPaid.map((receipt) => ({ _id: receipt._id })),
      payment_type: paymentType,
      payment_date: paymentDate,
      note: paymentNote,
    };

    axios
    .post(`${Helpers.baseUrl}/receipts/bulk-payment`, payments, Helpers.headers)
    .then((response) => {
      const paidSummary = receiptsBeingPaid.map((receipt) => {
        const totalPrice = Number(receipt.totalPrice) || 0;
        const currentPaid = Number(receipt.paid) || 0;
        const currentRemaining = Number(receipt.remaining);
        const paidNow =
          Number.isFinite(currentRemaining) && currentRemaining > 0
            ? currentRemaining
            : Math.max(totalPrice - currentPaid, 0);
        const nextPaid =
          totalPrice > 0
            ? Math.min(totalPrice, currentPaid + paidNow)
            : currentPaid + paidNow;

        return {
          ...receipt,
          status: "Paid",
          paymentType: submittedPaymentType,
          paid: Number(nextPaid.toFixed(2)),
          remaining: 0,
          bulkPaymentAmount: Number(paidNow.toFixed(2)),
          bulkPaymentDate: submittedPaymentDate,
          bulkPaymentType: submittedPaymentType,
        };
      });

      setPrintSummaryMode("paid");
      setPaidSummaryReceipts(paidSummary);
      Helpers.toast("success", "Payment Added successfully");
      getReceipts();
      getBulkPayments();
      setSelectedReceiptIds(new Set());
      resetBulkPaymentForm();
      setIsPaymentModelVisible(false);
      setTimeout(() => window.print(), 100);
    })
    .catch((error) => {
      Helpers.unauthenticated(error, navigate);
      setErrors(Helpers.error_response(error));
    })
    .finally(() => setBulkPaymentSaving(false));
  };

  const handleSelectPaymentType = (selectedPaymentType) => {
    setSelectedPaymentType(selectedPaymentType);
    setPaymentType(selectedPaymentType?.value);
  };

  useEffect(() => {
    getReceipts();
    getBulkPayments();
    getPerms();

  }, [getBulkPayments, getReceipts]);

  useEffect(() => {
    const resetPrintSummary = () => {
      setPrintSummaryMode("unpaid");
      setPaidSummaryReceipts([]);
    };

    window.addEventListener("afterprint", resetPrintSummary);
    return () => window.removeEventListener("afterprint", resetPrintSummary);
  }, []);

  const money = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? `$ ${v.toFixed(2)}` : "$ 0.00";
  };

  const getInvoiceDisplay = (receipt) =>
    receipt?.friendlyNumber ||
    (receipt?.invoiceNumber ? String(receipt.invoiceNumber) : "") ||
    (receipt?._id ? `INV-${receipt._id.slice(-6).toUpperCase()}` : "--");

  const getVehicleLabel = (vehicle) =>
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ");

  const getVehicleVin = (vehicle) => vehicle?.vin_number || vehicle?.vin || "";

  const getVehicleDisplay = (vehicle) =>
    [getVehicleLabel(vehicle), getVehicleVin(vehicle)]
      .filter(Boolean)
      .join(" ");

  const paymentTypeSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderColor: state.isFocused ? "#1976d2" : "#c4c4c4",
      boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#1976d2" : "#9e9e9e",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: 2,
      paddingBottom: 2,
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 1600,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 1600,
    }),
  };

  const getCalendarDate = (value) => {
    if (!value) return null;
    const dateMatch =
      typeof value === "string"
        ? value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        : null;

    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
    );
  };

  const formatReceiptDate = (value, options = {}) => {
    const date = getCalendarDate(value);
    if (!date) return "";
    return date.toLocaleDateString("en-US", options);
  };

  const getReceiptTotal = (receipt) => Number(receipt?.totalPrice) || 0;

  const getReceiptPaid = (receipt) => {
    const total = getReceiptTotal(receipt);
    const paid = Number(receipt?.paid) || 0;
    if (receipt?.status === "Paid") return total;
    return total > 0 ? Math.min(paid, total) : paid;
  };

  const getReceiptRemaining = (receipt) => {
    if (receipt?.status === "Paid") return 0;

    const remaining = Number(receipt?.remaining);
    if (Number.isFinite(remaining)) {
      return Math.max(remaining, 0);
    }

    return Math.max(getReceiptTotal(receipt) - getReceiptPaid(receipt), 0);
  };

  const filteredReceipts = receipts.filter((receipt) => {
    const term = search.trim().toLowerCase();
    const textMatch =
      !term ||
      getVehicleDisplay(receipt.vehicle).toLowerCase().includes(term) ||
      getInvoiceDisplay(receipt).toLowerCase().includes(term) ||
      String(receipt.totalPrice || "").toLowerCase().includes(term) ||
      String(receipt.paid || "").toLowerCase().includes(term) ||
      String(receipt.remaining || "").toLowerCase().includes(term) ||
      (receipt.status || "").toLowerCase().includes(term);

    const receiptDate = getCalendarDate(receipt.date);
    const from = fromDate ? getCalendarDate(fromDate) : null;
    const to = toDate ? getCalendarDate(toDate) : null;

    const dateMatch =
      (!from || (receiptDate && receiptDate >= from)) &&
      (!to || (receiptDate && receiptDate <= to));

    return textMatch && dateMatch;
  });

  const totals = filteredReceipts.reduce(
    (acc, r) => {
      acc.total += getReceiptTotal(r);
      acc.paid += getReceiptPaid(r);
      acc.remaining += getReceiptRemaining(r);
      return acc;
    },
    { total: 0, paid: 0, remaining: 0 }
  );

  const filteredUnpaid = filteredReceipts.filter((r) => r.status !== "Paid");
  const filteredUnpaidReceiptIds = filteredUnpaid.map((receipt) => receipt._id);
  const selectedUnpaidReceipts = filteredUnpaid.filter(
    (receipt) => selectedReceiptIds.has(receipt._id) && receipt.status !== "Paid"
  );
  const filteredUnpaidReceiptIdKey = filteredUnpaidReceiptIds.join("|");

  useEffect(() => {
    const allowedIds = new Set(
      filteredUnpaidReceiptIdKey
        ? filteredUnpaidReceiptIdKey.split("|")
        : []
    );

    setSelectedReceiptIds((prevIds) => {
      let changed = false;
      const nextIds = new Set();

      prevIds.forEach((id) => {
        if (allowedIds.has(id)) {
          nextIds.add(id);
        } else {
          changed = true;
        }
      });

      return changed ? nextIds : prevIds;
    });
  }, [filteredUnpaidReceiptIdKey]);

  const selectedTotals = selectedUnpaidReceipts.reduce(
    (acc, receipt) => {
      acc.total += getReceiptTotal(receipt);
      acc.paid += getReceiptPaid(receipt);
      acc.remaining += getReceiptRemaining(receipt);
      return acc;
    },
    { total: 0, paid: 0, remaining: 0 }
  );
  const printReceipts =
    printSummaryMode === "paid" ? paidSummaryReceipts : selectedUnpaidReceipts;
  const printTotals = printReceipts.reduce(
    (acc, receipt) => {
      acc.total += getReceiptTotal(receipt);
      acc.paid +=
        printSummaryMode === "paid"
          ? Number(receipt?.bulkPaymentAmount) || 0
          : getReceiptPaid(receipt);
      acc.remaining +=
        printSummaryMode === "paid" ? 0 : getReceiptRemaining(receipt);
      return acc;
    },
    { total: 0, paid: 0, remaining: 0 }
  );
  const printDocTitle =
    printSummaryMode === "paid" ? "PAID INVOICE SUMMARY" : "UNPAID INVOICES";
  const printCountLabel =
    printSummaryMode === "paid" ? "Paid Invoices" : "Selected Invoices";
  const printPaidHeader = printSummaryMode === "paid" ? "Paid Now" : "Paid";
  const printDueHeader = printSummaryMode === "paid" ? "Balance" : "Due";
  const bulkPaymentMeta = paidSummaryReceipts[0] || {};

  const rows = filteredReceipts.map((receipt) => ({
    ...receipt,
    id: receipt._id,
    invoiceDisplay: getInvoiceDisplay(receipt),
  }));
  const filteredBulkPayments = bulkPayments.filter((payment) => {
    const term = bulkPaymentSearch.trim().toLowerCase();
    const paymentDate = getCalendarDate(payment.paymentDate || payment.createdAt);
    const from = bulkPaymentFromDate ? getCalendarDate(bulkPaymentFromDate) : null;
    const to = bulkPaymentToDate ? getCalendarDate(bulkPaymentToDate) : null;
    const text = [
      payment.friendlyNumber,
      payment.customer?.name,
      payment.paymentType,
      payment.note,
      payment.totalAmount,
      payment.receiptCount,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const textMatch = !term || text.includes(term);
    const dateMatch =
      (!from || (paymentDate && paymentDate >= from)) &&
      (!to || (paymentDate && paymentDate <= to));

    return textMatch && dateMatch;
  });
  const bulkPaymentSummary = filteredBulkPayments.reduce(
    (acc, payment) => {
      acc.count += 1;
      acc.invoices += Number(payment.receiptCount) || 0;
      acc.amount += Number(payment.totalAmount) || 0;
      return acc;
    },
    { count: 0, invoices: 0, amount: 0 }
  );
  const bulkPaymentRows = filteredBulkPayments.map((payment) => ({
    ...payment,
    id: payment._id,
  }));
  const printedAt = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const columns = [
    {
      field: "select",
      headerName: (
        <div className="form-check d-flex justify-content-center mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            onChange={(e) => handleToggleAll(e.target.checked)}
            checked={
              filteredUnpaidReceiptIds.length > 0 &&
              filteredUnpaidReceiptIds.every((id) => selectedReceiptIds.has(id))
            }
          />
        </div>
      ),
      width: 70,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) =>
        params.row.status !== "Paid" &&
        (perms.can_update === 1 || Helpers.authUser.user_role == null) ? (
          <div className="form-check d-flex justify-content-center mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              value={params.row._id}
              onChange={() => handleCheckBox(params.row._id)}
              checked={selectedReceiptIds.has(params.row._id)}
            />
          </div>
        ) : (
          ""
        ),
    },
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: (params) =>
        params.row.date
          ? formatReceiptDate(params.row.date, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
    {
      field: "invoiceDisplay",
      headerName: "Invoice #",
      width: 140,
      sortable: false,
      filterable: false,
    },
    {
      field: "vin",
      headerName: "VIN",
      minWidth: 180,
      renderCell: (params) => getVehicleVin(params.row.vehicle),
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => getVehicleLabel(params.row.vehicle),
    },
    { field: "totalPrice", headerName: "Total", width: 110, renderCell: (params) => money(getReceiptTotal(params.row)) },
    { field: "paid", headerName: "Paid", width: 110, renderCell: (params) => money(getReceiptPaid(params.row)) },
    { field: "remaining", headerName: "Due", width: 110, renderCell: (params) => money(getReceiptRemaining(params.row)) },
    { field: "status", headerName: "Status", width: 110 },
    {
      field: "actions",
      headerName: "Actions",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          {params.row.status !== "Paid" &&
          (perms.can_update === 1 || Helpers.authUser.user_role == null) ? (
            <Link
              className="btn btn-warning btn-sm m-1"
              to={`/user/unpaid-receipt/make-payment/${params.row._id}`}
            >
              Make Payment
            </Link>
          ) : (
            ""
          )}
          <Link
            to={`/user/receipts/receipt-details/${params.row._id}`}
            className="btn btn-success btn-sm m-1"
          >
            <ion-icon name="eye-outline"></ion-icon>
          </Link>
          {perms.can_delete === 1 || Helpers.authUser.user_role == null ? (
            <button
              onClick={() => handleDelete(params.row._id)}
              type="button"
              className="btn btn-danger btn-sm m-1"
            >
              <ion-icon name="trash-outline"></ion-icon>
            </button>
          ) : null}
        </>
      ),
    },
  ];

  const bulkPaymentColumns = [
    {
      field: "friendlyNumber",
      headerName: "Bulk #",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "paymentDate",
      headerName: "Payment Date",
      width: 150,
      renderCell: (params) =>
        formatReceiptDate(params.value, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }) || "-",
    },
    {
      field: "receiptCount",
      headerName: "Invoices",
      width: 110,
      renderCell: (params) => Number(params.value || 0),
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      width: 130,
      renderCell: (params) => money(params.value),
    },
    {
      field: "paymentType",
      headerName: "Method",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value || "Unknown"} size="small" />
      ),
    },
    {
      field: "note",
      headerName: "Notes",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          component={Link}
          to={`/user/receipts/customer/${customer_name}/${customer_id}/bulk-payments/${params.row._id}`}
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="page-content">
      <div className="customer-receipts-screen">
      <div className="container-fluid">
        <div
          style={{
            marginBottom: 16,
            padding: 24,
            borderRadius: 12,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 280,
              height: 280,
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.08)",
              transform: "translate(30%, -30%)",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              zIndex: 1,
            }}
          >
            <h2 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
              All Receipts for {customer_name}
            </h2>
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 20,
                padding: "4px 12px",
                background: "rgba(255,255,255,0.15)",
              }}
            >
              {activeTab === "receipts"
                ? `${filteredReceipts.length} items`
                : `${filteredBulkPayments.length} payments`}
            </span>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body border-bottom">
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                    sx={{
                      minHeight: 44,
                      "& .MuiTab-root": {
                        minHeight: 44,
                        textTransform: "none",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Tab
                      value="receipts"
                      icon={<ReceiptLongIcon fontSize="small" />}
                      iconPosition="start"
                      label="Receipts"
                    />
                    <Tab
                      value="bulkPayments"
                      icon={<PaymentsIcon fontSize="small" />}
                      iconPosition="start"
                      label="Payment History"
                    />
                  </Tabs>
                </Box>

                {activeTab === "receipts" && (
                  <>
                    <div
                      className="row g-2"
                      style={{ alignItems: "end", marginBottom: 12 }}
                    >
                      <div className="col-lg-3 col-md-4">
                        <label>From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-3 col-md-4">
                        <label>To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4">
                        <label>Search</label>
                        <input
                          className="form-control"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-2 col-md-12">
                        <div className="d-grid gap-2">
                          {selectedUnpaidReceipts.length > 0 ? (
                            <>
                              <button
                                className="btn btn-success w-100"
                                onClick={handleBulkUpdate}
                              >
                                Make Bulk Payment
                              </button>
                              <button
                                className="btn btn-outline-primary w-100"
                                onClick={handlePrintSelected}
                              >
                                Print Selected
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-light w-100" disabled>
                                Make Bulk Payment
                              </button>
                              <button className="btn btn-light w-100" disabled>
                                Print Selected
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedUnpaidReceipts.length > 0 && (
                      <div className="row mb-3">
                        <div className="col-md-4 mb-2">
                          <div className="p-3 border rounded bg-light h-100">
                            <div className="text-muted">Selected Invoices</div>
                            <div className="fw-semibold">{selectedUnpaidReceipts.length}</div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <div className="p-3 border rounded bg-light h-100">
                            <div className="text-muted">Selected Invoice Total</div>
                            <div className="fw-semibold">{money(selectedTotals.total)}</div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <div className="p-3 border rounded bg-light h-100">
                            <div className="text-muted">Selected Balance Due</div>
                            <div className="fw-semibold">{money(selectedTotals.remaining)}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Total Business</div>
                          <div className="fw-semibold">{money(totals.total)}</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Total Paid</div>
                          <div className="fw-semibold">{money(totals.paid)}</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Total Remaining</div>
                          <div className="fw-semibold">{money(totals.remaining)}</div>
                        </div>
                      </div>
                    </div>
                    <ReusableDataGrid
                      rows={rows}
                      columns={columns}
                      pageSize={10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      noRowsMessage="No data available to display"
                    />
                  </>
                )}

                {activeTab === "bulkPayments" && (
                  <>
                    <div
                      className="row g-2"
                      style={{ alignItems: "end", marginBottom: 12 }}
                    >
                      <div className="col-lg-3 col-md-4">
                        <label>From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={bulkPaymentFromDate}
                          onChange={(e) => setBulkPaymentFromDate(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-3 col-md-4">
                        <label>To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={bulkPaymentToDate}
                          onChange={(e) => setBulkPaymentToDate(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4">
                        <label>Search</label>
                        <input
                          className="form-control"
                          value={bulkPaymentSearch}
                          onChange={(e) => setBulkPaymentSearch(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-2 col-md-12">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={getBulkPayments}
                        >
                          Refresh
                        </button>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Payments</div>
                          <div className="fw-semibold">{bulkPaymentSummary.count}</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Invoices Paid</div>
                          <div className="fw-semibold">{bulkPaymentSummary.invoices}</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <div className="p-3 border rounded bg-light h-100">
                          <div className="text-muted">Total Collected</div>
                          <div className="fw-semibold">{money(bulkPaymentSummary.amount)}</div>
                        </div>
                      </div>
                    </div>

                    <ReusableDataGrid
                      rows={bulkPaymentRows}
                      columns={bulkPaymentColumns}
                      loading={bulkPaymentsLoading}
                      pageSize={10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      noRowsMessage="No payment history found"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <div className="customer-receipts-print">
        <div className="print-header">
          <div className="print-company">
            {shopLogoSrc ? (
              <img
                src={shopLogoSrc}
                alt="Company Logo"
                className="print-company-logo"
              />
            ) : (
              <div className="print-company-name">
                {Helpers.authShop?.name || "Shop"}
              </div>
            )}
            <div className="print-company-contact">
              <div style={{ whiteSpace: "pre-line" }}>
                {Helpers.authShop?.address?.replace(", ", ",\n") || ""}
              </div>
              <div>{Helpers.authShop?.phone || ""}</div>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-doc-title">{printDocTitle}</div>
            <div className="print-meta" style={{ textAlign: "right" }}>
              <div>Printed: {printedAt}</div>
              <div>Customer: {customer_name}</div>
              <div>{printCountLabel}: {printReceipts.length}</div>
              {printSummaryMode === "paid" ? (
                <>
                  <div>
                    Payment Date:{" "}
                    {bulkPaymentMeta.bulkPaymentDate
                      ? formatReceiptDate(bulkPaymentMeta.bulkPaymentDate, {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })
                      : ""}
                  </div>
                  <div>Payment Type: {bulkPaymentMeta.bulkPaymentType || ""}</div>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <table>
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "23%" }} />
            <col style={{ width: "21%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice</th>
              <th>VIN</th>
              <th>Vehicle</th>
              <th>Total</th>
              <th>{printPaidHeader}</th>
              <th>{printDueHeader}</th>
            </tr>
          </thead>
          <tbody>
            {printReceipts.map((receipt) => (
              <tr key={receipt._id}>
                <td className="col-date">
                  {receipt.date
                    ? formatReceiptDate(receipt.date, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : ""}
                </td>
                <td className="col-invoice">{getInvoiceDisplay(receipt)}</td>
                <td className="col-vin">{getVehicleVin(receipt.vehicle) || "-"}</td>
                <td className="col-vehicle">{getVehicleLabel(receipt.vehicle) || "-"}</td>
                <td className="col-total">{money(getReceiptTotal(receipt))}</td>
                <td className="col-paid">
                  {money(
                    printSummaryMode === "paid"
                      ? receipt.bulkPaymentAmount
                      : getReceiptPaid(receipt)
                  )}
                </td>
                <td className="col-due">
                  {money(printSummaryMode === "paid" ? 0 : getReceiptRemaining(receipt))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="4">Totals</td>
              <td className="col-total">{money(printTotals.total)}</td>
              <td className="col-paid">{money(printTotals.paid)}</td>
              <td className="col-due">{money(printTotals.remaining)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Dialog
        open={isPaymentModelVisible}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          closeBulkPaymentModal();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            pt: 2,
            px: 3,
            flexShrink: 0,
            color: "inherit",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "200px",
              height: "200px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, fontWeight: 600 }}>
            Bulk Payment
          </Box>
          <IconButton
            aria-label="close"
            onClick={closeBulkPaymentModal}
            disabled={bulkPaymentSaving}
            sx={{
              color: "white",
              position: "relative",
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Selected Invoices"
              value={selectedUnpaidReceipts.length}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              helperText={errors.receipts ? errors.receipts : ""}
              error={Boolean(errors.receipts)}
            />
            <TextField
              label="Selected Invoice Total"
              value={money(selectedTotals.total)}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Selected Balance Due"
              value={money(selectedTotals.remaining)}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <Box>
              <label className="form-label">Payment Type</label>
              <Select
                options={paymentTypeOptions}
                value={selectedPaymentType}
                onChange={handleSelectPaymentType}
                isClearable={true}
                styles={paymentTypeSelectStyles}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                menuShouldScrollIntoView={false}
              />
              <small className="text-danger">
                {errors.payment_type ? errors.payment_type : ""}
              </small>
            </Box>
            <TextField
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText={errors.payment_date ? errors.payment_date : ""}
              error={Boolean(errors.payment_date)}
            />
            <TextField
              label="Notes"
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Who paid, check number, reference, or other details"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
          <Button
            onClick={closeBulkPaymentModal}
            variant="outlined"
            color="error"
            disabled={bulkPaymentSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={makeBulkPayment}
            variant="contained"
            disabled={bulkPaymentSaving}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              backgroundColor: "#28a745",
              "&:hover": {
                backgroundColor: "#218838",
              },
            }}
          >
            {bulkPaymentSaving ? "Saving..." : "Make Payment"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CustomerReceipts;
